import type { ColumnsType } from 'antd/es/table'
import {
  ReloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons'
import {
  Table,
  Card,
  Tag,
  Space,
  Typography,
  Tooltip,
  Button,
  Row,
  Col,
  Statistic,
  Alert,
  Progress
} from 'antd'
import { useQuery } from '@tanstack/react-query'
import { fmtDateTime } from '../utils'
import $api from '../../../API'

const { Title } = Typography

interface PullWorkflowExecution {
  id: number
  started_at: string
  ended_at?: string
  status: 'running' | 'success' | 'error'
  duration_ms?: number
  error_message?: string
  locations_processed: number
  epay_groups_processed: number
  premium_groups_processed: number
  coverage_categories_processed: number
  coverage_links_processed: number
  paid_through_updates: number
  lucent_ar_records: number
  legacy_ar_records: number
  lucent_invoices: number
  lucent_invoice_lines: number
  legacy_invoices: number
  legacy_invoice_lines: number
  workflow_id?: string
  run_id?: string
}

interface PullWorkflowSummary {
  total_executions: number
  successful_executions: number
  failed_executions: number
  running_executions: number
  avg_duration_ms: number
  max_duration_ms: number
  last_execution_at: string
  is_stale: boolean
  hours_since_last: number
}

interface PullWorkflowResponse {
  executions: PullWorkflowExecution[]
  summary: PullWorkflowSummary
  range: {
    from: string
    to: string
  }
}

const usePullWorkflowStatus = () => {
  return useQuery({
    queryKey: ['healthcheck', 'pull-workflow'],
    queryFn: async (): Promise<PullWorkflowResponse> => {
      const response = await $api.get<PullWorkflowResponse>('/healthcheck/pull-workflow/status')
      return response.data
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  })
}

const statusTag = (status: string) => {
  switch (status) {
    case 'success':
      return (
        <Tag color='green' icon={<CheckCircleOutlined />}>
          Success
        </Tag>
      )
    case 'error':
      return (
        <Tag color='red' icon={<ExclamationCircleOutlined />}>
          Error
        </Tag>
      )
    case 'running':
      return (
        <Tag color='blue' icon={<ClockCircleOutlined />}>
          Running
        </Tag>
      )
    default:
      return <Tag>{status}</Tag>
  }
}

const formatDuration = (ms?: number) => {
  if (!ms) return '—'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60000).toFixed(1)}m`
}

const LastExecutionsTable: React.FC<{
  data: PullWorkflowExecution[]
  loading: boolean
  onReload: () => void
}> = ({ data, loading, onReload }) => {
  const columns: ColumnsType<PullWorkflowExecution> = [
    {
      title: 'Execution',
      key: 'execution',
      render: (_, r) => (
        <Space direction='vertical' size={0}>
          <Space>
            <Typography.Text strong>#{r.id}</Typography.Text>
            {statusTag(r.status)}
          </Space>
          <Typography.Text type='secondary' style={{ fontSize: 12 }}>
            started {fmtDateTime(r.started_at)}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_, r) => (
        <Space direction='vertical' size={0}>
          <Typography.Text>{formatDuration(r.duration_ms)}</Typography.Text>
          {r.ended_at && (
            <Typography.Text type='secondary' style={{ fontSize: 12 }}>
              ended {fmtDateTime(r.ended_at)}
            </Typography.Text>
          )}
        </Space>
      )
    },
    {
      title: 'Records Processed',
      key: 'records',
      render: (_, r) => (
        <Space direction='vertical' size={2}>
          <Typography.Text>
            Locations: <Typography.Text code>{r.locations_processed}</Typography.Text>
          </Typography.Text>
          <Typography.Text>
            Groups:{' '}
            <Typography.Text code>
              {r.epay_groups_processed + r.premium_groups_processed}
            </Typography.Text>
          </Typography.Text>
          <Typography.Text>
            Invoices:{' '}
            <Typography.Text code>{r.lucent_invoices + r.legacy_invoices}</Typography.Text>
          </Typography.Text>
        </Space>
      )
    },
    {
      title: 'Error',
      dataIndex: 'error_message',
      key: 'error_message',
      ellipsis: true,
      render: (v: string) =>
        v ? (
          <Tooltip title={v}>
            <Typography.Text type='danger'>{v}</Typography.Text>
          </Tooltip>
        ) : (
          '—'
        )
    }
  ]

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Last 5 Pull Workflow Executions
          </Typography.Title>
          <Tooltip title='Reload'>
            <Button icon={<ReloadOutlined />} onClick={onReload} size='small' />
          </Tooltip>
        </Space>
      }
      className='rounded-2xl'
    >
      <Table<PullWorkflowExecution>
        rowKey={r => r.id}
        dataSource={data}
        columns={columns}
        loading={loading}
        pagination={false}
        size='middle'
      />
    </Card>
  )
}

export const PullWorkflowDashboard: React.FC = () => {
  const { data, isLoading, isError, error, refetch, isFetching } = usePullWorkflowStatus()

  const executions = data?.executions ?? []
  const summary = data?.summary

  const successRate = summary?.total_executions
    ? Math.round((summary.successful_executions / summary.total_executions) * 100)
    : 0

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Pull Workflow Status</Title>
      <Space direction='vertical' size={16} style={{ width: '100%' }}>
        {isError && (
          <Alert
            type='error'
            showIcon
            message='Failed to load'
            description={(error as Error)?.message ?? String(error)}
          />
        )}

        {summary?.is_stale && (
          <Alert
            type='warning'
            showIcon
            message='Workflow appears stale'
            description={`Last execution was ${summary.hours_since_last} hours ago. Expected daily execution.`}
          />
        )}

        <Row gutter={[16, 16]}>
          <Col xs={24} md={6}>
            <Card className='rounded-2xl'>
              <Statistic
                title='Success Rate (7d)'
                value={successRate}
                suffix='%'
                loading={isLoading || isFetching}
                valueStyle={{
                  color: successRate >= 95 ? '#3f8600' : successRate >= 80 ? '#faad14' : '#cf1322'
                }}
              />
              <Progress
                percent={successRate}
                showInfo={false}
                strokeColor={
                  successRate >= 95 ? '#3f8600' : successRate >= 80 ? '#faad14' : '#cf1322'
                }
                size='small'
              />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card className='rounded-2xl'>
              <Statistic
                title='Total Executions (7d)'
                value={summary?.total_executions || 0}
                loading={isLoading || isFetching}
              />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card className='rounded-2xl'>
              <Statistic
                title='Avg Duration'
                value={formatDuration(summary?.avg_duration_ms)}
                loading={isLoading || isFetching}
              />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card className='rounded-2xl'>
              <Statistic
                title='Failed Executions'
                value={summary?.failed_executions || 0}
                loading={isLoading || isFetching}
                valueStyle={{
                  color: (summary?.failed_executions || 0) > 0 ? '#cf1322' : '#3f8600'
                }}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card className='rounded-2xl'>
              <Space>
                <InfoCircleOutlined />
                <Typography.Text>
                  Monitoring window: {fmtDateTime(data?.range.from)} → {fmtDateTime(data?.range.to)}
                </Typography.Text>
              </Space>
            </Card>
          </Col>
        </Row>

        <LastExecutionsTable
          data={executions}
          loading={isLoading || isFetching}
          onReload={refetch}
        />
      </Space>
    </div>
  )
}
